'use client';
import { Fragment } from 'react';
import { LoginWhite } from '@/app/components/icons/LoginWhite';
import { ClientLink } from '@/app/components/shared/ClientLink';
import { ReportEvent } from '@/app/components/shared/Footer/ReportEvent';
import type { RegistrationConfig } from '@/app/components/shared/Footer/types';
import {
  getFooterConfig,
  getRegistrationConfig,
  getSocialLinks,
  getTermsAndPrivacy,
} from '@/app/components/shared/Footer/utils';
import { COPY_RIGHT, isEnterpriseDistribution, SITE } from '@/app/constants';
import type { SiteType } from '@/app/types';

export const getLegalSectionClassName = (site: SiteType) =>
  site === 'china'
    ? 'flex flex-col items-center gap-3 text-center'
    : 'flex flex-wrap items-center justify-between gap-4';

/**
 * 首页专用页脚：左侧 logo + 社交图标 + 二维码墙，右侧链接分栏，底部备案与版权。
 *
 * 只在首页（[locale]/page.tsx）使用，不动 shared/Footer —— 后者被 16 个页面共用，
 * 其中 blog / tools 三处走 theme="light" 白底，改共用组件会连带影响它们。
 * 链接与备案信息全部复用 shared/Footer/utils 的既有配置，保持与全站一致。
 */
export const HomeFooter = ({ className }: { className?: string }) => {
  if (isEnterpriseDistribution) return null;

  const socialLinks = getSocialLinks();
  const footerConfig = getFooterConfig();
  const termsAndPrivacy = getTermsAndPrivacy();
  const registrationConfig = getRegistrationConfig();

  const renderLegal = (item: RegistrationConfig, index: number, length: number) => (
    <Fragment key={item.id}>
      {item.href ? (
        <ClientLink
          href={item.href}
          target={item.target}
          rel={item.rel}
          className="flex items-center text-white/40 transition-colors hover:cursor-pointer hover:text-white/70"
        >
          {item.icon && <img src={item.icon} alt={item.alt} className="mr-1 h-4 w-4" />}
          {item.text}
        </ClientLink>
      ) : (
        <span className="text-white/40">{item.text}</span>
      )}
      {index < length - 1 && <span className="text-white/[0.16]">|</span>}
    </Fragment>
  );

  return (
    <footer data-google-interstitial="false" className={className}>
      <div className="mx-auto w-full max-w-[1600px] px-20 pt-24 pb-10 sm:px-4 md:px-6 md:pt-16">
        {/* 上半区：左 品牌/社交/二维码，右 链接分栏 */}
        <div className="flex justify-between gap-16 md:flex-col md:gap-12">
          <div className="shrink-0">
            {/* 与 header 同一枚 logo（header 深色态用 LoginWhite），尺寸沿用原来的 text-3xl */}
            <LoginWhite className="text-3xl text-white" />

            <div className="mt-10 flex flex-wrap gap-4 md:mt-8">
              {socialLinks.map(({ icon: Icon, href, label }) => (
                <ReportEvent key={`social_${label}`} eventName="element_click" params={{ element_name: label }}>
                  <ClientLink
                    href={href}
                    target="_blank"
                    title={label}
                    rel="noopener noreferrer"
                    className="flex size-8 items-center justify-center text-white/70 transition-colors hover:text-white"
                  >
                    <Icon className="size-6" />
                  </ClientLink>
                </ReportEvent>
              ))}
            </div>

            {/* 二维码墙：目前是占位图 */}
            {/* <div className="mt-10 flex flex-wrap gap-4 md:mt-8">
              {QR_CODES.map(({ id, src }) => (
                <div key={id} className="flex flex-col items-center gap-3">
                  <img
                    src={src}
                    alt={tQr(id)}
                    loading="lazy"
                    decoding="async"
                    className="size-[77px] rounded-8 bg-white p-1 md:size-[61px]"
                  />
                  <span className="text-[13px] text-white/50 md:text-[12px]">{tQr(id)}</span>
                </div>
              ))}
            </div> */}
          </div>

          <div className="flex gap-20 md:flex-wrap md:gap-x-12 md:gap-y-10 lg:gap-12">
            {footerConfig.map(({ title, children, id }) => (
              <div key={id} className="flex flex-col gap-3 text-[14px] leading-6">
                <div className="mb-1 font-medium text-[15px] text-white">{title}</div>
                {children.map(({ href, text, rel, target, reportElement }) => {
                  const link = (
                    <ClientLink
                      key={href}
                      href={href}
                      target={target}
                      rel={rel}
                      className="text-white/50 transition-colors hover:cursor-pointer hover:text-white"
                    >
                      {text}
                    </ClientLink>
                  );

                  return reportElement ? (
                    <ReportEvent
                      key={reportElement.key}
                      eventName={reportElement.eventName}
                      params={reportElement.params}
                    >
                      {link}
                    </ReportEvent>
                  ) : (
                    link
                  );
                })}
              </div>
            ))}
          </div>
        </div>

        {/* 下半区：备案 / 条款 / 版权 */}
        <div className="mt-20 border-white/[0.08] border-t pt-8 text-[13px] md:mt-12">
          {SITE === 'china' ? (
            <div className={getLegalSectionClassName(SITE)}>
              <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2">
                {termsAndPrivacy
                  .filter((item) => item.href)
                  .map((item, index, arr) => renderLegal(item, index, arr.length))}
              </div>
              <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2">
                {registrationConfig.map((item, index) => renderLegal(item, index, registrationConfig.length))}
              </div>
              <span className="text-white/40">{COPY_RIGHT}</span>
            </div>
          ) : (
            <div className={getLegalSectionClassName(SITE)}>
              <span className="text-white/40">{COPY_RIGHT}</span>
              <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
                {termsAndPrivacy
                  .filter((item): item is RegistrationConfig & { href: string } => Boolean(item.href))
                  .map(({ href, text, target, rel, id }) => (
                    <ClientLink
                      key={id}
                      href={href}
                      target={target}
                      rel={rel}
                      className="text-white/40 transition-colors hover:cursor-pointer hover:text-white/70"
                    >
                      {text}
                    </ClientLink>
                  ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </footer>
  );
};
